import React from "react";
import styled, { useTheme } from "styled-components";
import { useAuthContext } from "../Auth";
import {
  PrimaryButtonSmallV2,
  SecondaryButtonSmallV2,
} from "../Buttons/Buttons";
import {
  CardCalendarIcon,
  CardCompletionBadgeIcon,
  PlusIcon,
  MasterProductIcon,
  VariantProductIcon,
} from "../Icons/Icons";
import type { PIMProductMinimal } from "../../types/types.PIM";
import { useTranslation } from "react-i18next";
import { useStoreState } from "../../util/util";
import {
  StatusLeft,
  getProductStatusColor,
  getProductStatusText,
} from "../Status/Status";
import ReactTooltip from "react-tooltip";

interface CardProductProps {
  product: PIMProductMinimal;
  href?: string;
  onProductClick?: (
    product: PIMProductMinimal,
    event?: React.MouseEvent
  ) => void;
  onQuoteClick?: (product: PIMProductMinimal) => void;
  onSampleClick?: (product: PIMProductMinimal) => void;
  onDocumentClick?: (
    product: PIMProductMinimal,
    docType: "sds" | "tds" | "all"
  ) => void;
}

const CardWrapper = styled.a`
  position: relative;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.resolve("color_cardProduct_bg")};
  width: 100%;
  height: 100%;
  border-radius: ${({ theme }) => theme.resolve("radius_cardProduct")};
  box-shadow: ${({ theme }) => theme.resolve("effect_cardProduct_shadow_x")}
    ${({ theme }) => theme.resolve("effect_cardProduct_shadow_y")}
    ${({ theme }) => theme.resolve("effect_cardProduct_shadow_blur")}
    ${({ theme }) => theme.resolve("effect_cardProduct_shadow_spread")}
    ${({ theme }) => theme.resolve("effect_cardProduct_shadow_color")};
  transition: box-shadow 0.2s ease-in-out;
  cursor: pointer;
  overflow: hidden;
  text-decoration: none;
  color: inherit;
  user-select: none;

  /* Use pseudo-elements for borders to prevent content jitter */
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
    border-radius: inherit;
    border-top: ${({ theme }) =>
      `${theme.resolve("border_cardProduct_top")} solid ${theme.resolve(
        "color_cardProduct_border"
      )}`};
    border-right: ${({ theme }) =>
      `${theme.resolve("border_cardProduct_right")} solid ${theme.resolve(
        "color_cardProduct_border"
      )}`};
    border-bottom: ${({ theme }) =>
      `${theme.resolve("border_cardProduct_bottom")} solid ${theme.resolve(
        "color_cardProduct_border"
      )}`};
    border-left: ${({ theme }) =>
      `${theme.resolve("border_cardProduct_left")} solid ${theme.resolve(
        "color_cardProduct_border"
      )}`};
    transition: border-color 0.2s ease-in-out, border-width 0.2s ease-in-out;
    z-index: 1;
  }

  &:hover::before {
    border-top-width: ${({ theme }) =>
      theme.resolve("border_cardProduct_top_hover")};
    border-right-width: ${({ theme }) =>
      theme.resolve("border_cardProduct_right_hover")};
    border-bottom-width: ${({ theme }) =>
      theme.resolve("border_cardProduct_bottom_hover")};
    border-left-width: ${({ theme }) =>
      theme.resolve("border_cardProduct_left_hover")};
    border-color: ${({ theme }) =>
      theme.resolve("color_cardProduct_border_hover")};
  }

  /* Ensure content is above the border pseudo-element */
  & > * {
    position: relative;
    z-index: 2;
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: ${({ theme }) => theme.resolve("aspect_cardProduct_default")};
  overflow: hidden;
  border-top-left-radius: ${({ theme }) =>
    theme.resolve("radius_cardProduct_image")};
  border-top-right-radius: ${({ theme }) =>
    theme.resolve("radius_cardProduct_image")};
  background: ${({ theme }) => theme.resolve("color_cardProduct_bg")};

  /* Account for border width in positioning */
  margin-top: ${({ theme }) => theme.resolve("border_cardProduct_top")};
  margin-left: ${({ theme }) => theme.resolve("border_cardProduct_left")};
  margin-right: ${({ theme }) => theme.resolve("border_cardProduct_right")};
  width: ${({ theme }) => {
    const borderLeft = theme.resolve("border_cardProduct_left");
    const borderRight = theme.resolve("border_cardProduct_right");
    return `calc(100% - ${borderLeft} - ${borderRight})`;
  }};

  /* Styles for the primary product image (direct child only) */
  & > img {
    position: absolute;
    top: ${({ theme }) => theme.resolve("spacing_cardProduct_image_padding")};
    left: ${({ theme }) => theme.resolve("spacing_cardProduct_image_padding")};
    right: ${({ theme }) => theme.resolve("spacing_cardProduct_image_padding")};
    bottom: 0;
    width: ${({ theme }) => {
      const padding = theme.resolve("spacing_cardProduct_image_padding");
      return padding === "0px" ? "100%" : `calc(100% - ${padding} * 2)`;
    }};
    height: ${({ theme }) => {
      const padding = theme.resolve("spacing_cardProduct_image_padding");
      return padding === "0px" ? "100%" : `calc(100% - ${padding})`;
    }};
    object-fit: cover;
    border-radius: ${({ theme }) => {
      const padding = theme.resolve("spacing_cardProduct_image_padding");
      return padding === "0px"
        ? "0"
        : theme.resolve("radius_cardProduct_image");
    }};
  }
`;

const PlaceholderImage = styled.div`
  position: absolute;
  top: ${({ theme }) => theme.resolve("spacing_cardProduct_image_padding")};
  left: ${({ theme }) => theme.resolve("spacing_cardProduct_image_padding")};
  right: ${({ theme }) => theme.resolve("spacing_cardProduct_image_padding")};
  bottom: 0;
  width: ${({ theme }) => {
    const padding = theme.resolve("spacing_cardProduct_image_padding");
    return padding === "0px" ? "100%" : `calc(100% - ${padding} * 2)`;
  }};
  height: ${({ theme }) => {
    const padding = theme.resolve("spacing_cardProduct_image_padding");
    return padding === "0px" ? "100%" : `calc(100% - ${padding})`;
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  /* Create opaque background to prevent card background showing through */
  background: linear-gradient(
      ${({ theme }) => theme.resolve("color_brand_15")},
      ${({ theme }) => theme.resolve("color_brand_15")}
    ),
    white;
  border-radius: ${({ theme }) => {
    const padding = theme.resolve("spacing_cardProduct_image_padding");
    return padding === "0px" ? "0" : theme.resolve("radius_cardProduct_image");
  }};
`;

const PlaceholderLogo = styled.img`
  max-width: 60%;
  max-height: 60%;
  object-fit: contain;
  display: block;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
  padding: ${({ theme }) => theme.resolve("spacing_lg")}
    ${({ theme }) => theme.resolve("spacing_xl")}
    ${({ theme }) => theme.resolve("spacing_xl")};
`;

const ProductName = styled.h3`
  margin: 0;
  font-family: ${({ theme }) => theme.resolve("font_family_primary")};
  font-weight: ${({ theme }) => theme.resolve("font_weight_semibold")};
  font-size: ${({ theme }) => theme.resolve("font_size_base")};
  line-height: ${({ theme }) => theme.resolve("line_height_base")};
  color: ${({ theme }) => theme.resolve("color_cardProduct_text_product_name")};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const AttributesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_cardProduct_gap")};
  padding: ${({ theme }) => theme.resolve("spacing_md")} 0;
`;

const DocIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="12"
    height="13"
    viewBox="0 0 12 13"
    fill="none"
  >
    <g clipPath="url(#clip0_1608_2534)">
      <path
        d="M2.34961 1.8125C2.34961 1.39829 2.68539 1.0625 3.09961 1.0625H8.0996C8.12935 1.0625 8.15785 1.07431 8.1789 1.09534L11.4167 4.33321C11.4378 4.35424 11.4496 4.38276 11.4496 4.4125V11.8125C11.4496 12.2267 11.1138 12.5625 10.6996 12.5625H3.09961C2.68539 12.5625 2.34961 12.2267 2.34961 11.8125V1.8125Z"
        stroke="#0F1D2B"
        strokeWidth="0.75"
      />
      <path
        d="M8.09961 0.962524V3.41252C8.09961 3.96481 8.54731 4.41252 9.09961 4.41252H11.5496"
        stroke="#0F1D2B"
        strokeWidth="0.75"
      />
      <path
        d="M7 6.11255H1C0.723858 6.11255 0.5 6.33641 0.5 6.61255V10.6125C0.5 10.8887 0.723858 11.1125 1 11.1125H7C7.27614 11.1125 7.5 10.8887 7.5 10.6125V6.61255C7.5 6.33641 7.27614 6.11255 7 6.11255Z"
        fill="#DD2025"
      />
      <path
        d="M1.24969 9.61255V7.4307H2.11049C2.27597 7.4307 2.41695 7.4623 2.53343 7.52555C2.6499 7.58805 2.73868 7.67505 2.79976 7.78655C2.86155 7.89735 2.89245 8.0252 2.89245 8.17005C2.89245 8.31495 2.8612 8.4428 2.7987 8.5536C2.7362 8.6644 2.64564 8.7507 2.52703 8.81245C2.40913 8.87425 2.26638 8.90515 2.09876 8.90515H1.55011V8.5355H2.02419C2.11297 8.5355 2.18612 8.5202 2.24365 8.48965C2.30189 8.4584 2.34522 8.41545 2.37363 8.36075C2.40275 8.30535 2.4173 8.2418 2.4173 8.17005C2.4173 8.0976 2.40275 8.0344 2.37363 7.98045C2.34522 7.92575 2.30189 7.8835 2.24365 7.85365C2.18541 7.8231 2.11155 7.80785 2.02206 7.80785H1.71098V9.61255H1.24969ZM3.96551 9.61255H3.19207V7.4307H3.9719C4.19136 7.4307 4.38028 7.4744 4.53866 7.56175C4.69704 7.6484 4.81885 7.77305 4.90408 7.9357C4.99002 8.09835 5.033 8.29295 5.033 8.5195C5.033 8.74675 4.99002 8.9421 4.90408 9.10545C4.81885 9.2688 4.69634 9.39415 4.53653 9.4815C4.37744 9.56885 4.1871 9.61255 3.96551 9.61255ZM3.65336 9.2173H3.94634C4.0827 9.2173 4.1974 9.19315 4.29044 9.14485C4.38419 9.09585 4.4545 9.0202 4.50138 8.91795C4.54896 8.81495 4.57275 8.68215 4.57275 8.5195C4.57275 8.3583 4.54896 8.22655 4.50138 8.12425C4.4545 8.022 4.38454 7.9467 4.2915 7.8984C4.19846 7.8501 4.08376 7.82595 3.9474 7.82595H3.65336V9.2173ZM5.3747 9.61255V7.4307H6.8193V7.81105H5.836V8.33095H6.7234V8.71125H5.836V9.61255H5.3747Z"
        fill="white"
      />
    </g>
    <defs>
      <clipPath id="clip0_1608_2534">
        <rect
          width="12"
          height="12"
          fill="white"
          transform="translate(0 0.8125)"
        />
      </clipPath>
    </defs>
  </svg>
);

const DocBadges = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
  flex-wrap: wrap;
`;

const DocBadge = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
  padding: ${({ theme }) => theme.resolve("spacing_xs")};
  border-radius: ${({ theme }) => theme.resolve("radius_button_standard")};
  background: ${({ theme }) =>
    theme.resolve("color_button_bg_tertiary_enabled")};
  border: 1px solid
    ${({ theme }) => theme.resolve("color_button_border_tertiary_enabled")};
  font-family: ${({ theme }) => theme.resolve("font_family_primary")};
  font-size: ${({ theme }) => theme.resolve("font_size_xs")};
  line-height: ${({ theme }) => theme.resolve("line_height_xs")};
  font-weight: ${({ theme }) => theme.resolve("font_weight_medium")};
  color: ${({ theme }) => theme.resolve("color_button_text_tertiary_enabled")};
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  &:hover {
    background: ${({ theme }) =>
      theme.resolve("color_button_bg_tertiary_hover")};
    border-color: ${({ theme }) =>
      theme.resolve("color_button_border_tertiary_hover")};
  }
`;

const Actions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.resolve("spacing_md")};
  flex-wrap: wrap;
`;

const ActionsWrapper = styled.div`
  margin-top: auto;
`;

const AdminMetadata = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.resolve("spacing_xxs")} 0;
  font-size: ${({ theme }) => theme.resolve("font_size_xs")};
  color: ${({ theme }) => theme.resolve("color_text_tertiary")};
`;

const AdminMetadataLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
`;

const AdminMetadataRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
  font-size: ${({ theme }) => theme.resolve("font_size_xs")};
  line-height: ${({ theme }) => theme.resolve("line_height_xs")};
  font-weight: ${({ theme }) => theme.resolve("font_weight_regular")};
  font-family: ${({ theme }) => theme.resolve("font_family_primary")};
  color: ${({ theme }) =>
    theme.resolve("color_cardProduct_text_attribute_value")};
`;

const AdminMetadataIcon = styled.div`
  width: 12px;
  height: 12px;
  flex-shrink: 0;
  color: ${({ theme }) => theme.resolve("color_cardProduct_icon")};
`;

const StatusContainer = styled.div`
  display: flex;
  align-self: flex-start;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};

  /* Reset color to allow StatusLeft to use its own semantic colors */
  color: initial;
`;

// Custom InfoBlock for CardProduct with 2-line limit and tooltips
const CardInfoBlockContainer = styled.div`
  max-width: 100%;
`;

const CardInfoBlockHeader = styled.div`
  font-weight: ${({ theme }) => theme.resolve("font_weight_regular")};
  font-size: ${({ theme }) => theme.resolve("font_size_xs")};
  color: ${({ theme }) =>
    theme.resolve("color_cardProduct_text_attribute_name")};
  line-height: ${({ theme }) => theme.resolve("line_height_xs")};
  font-family: ${({ theme }) => theme.resolve("font_family_primary")};
`;

const CardInfoBlockContent = styled.div<{ isDescription?: boolean }>`
  margin: 0;
  font-family: ${({ theme }) => theme.resolve("font_family_primary")};
  font-weight: ${({ theme }) => theme.resolve("font_weight_regular")};
  color: ${({ theme }) =>
    theme.resolve("color_cardProduct_text_attribute_value")};
  font-size: ${({ theme }) => theme.resolve("font_size_sm")};
  line-height: ${({ theme }) => theme.resolve("line_height_sm")};
  max-width: 100%;
  display: -webkit-box;
  -webkit-line-clamp: ${({ isDescription }) => (isDescription ? 5 : 2)};
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;

  /* Remove default margins from HTML elements to ensure consistent spacing */
  & > *:first-child {
    margin-top: 0;
  }

  & > *:last-child {
    margin-bottom: 0;
  }
`;

const MasterVariantBadgeContainer = styled.div`
  display: flex;
  padding: ${({ theme }) => theme.resolve("spacing_xs")}
    ${({ theme }) => theme.resolve("spacing_md")};
  justify-content: center;
  align-items: center;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
  border-radius: ${({ theme }) => theme.resolve("radius_xs")};
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_border_lighter")};
  background-color: ${({ theme }) =>
    theme.resolve("color_bg_surface_standard")};
  position: absolute;
  right: ${({ theme }) =>
    `calc(${theme.resolve("spacing_xl")} + ${theme.resolve(
      "spacing_cardProduct_image_padding"
    )})`};
  top: ${({ theme }) =>
    `calc(${theme.resolve("spacing_xl")} + ${theme.resolve(
      "spacing_cardProduct_image_padding"
    )})`};
  > span {
    color: ${({ theme }) => theme.resolve("color_text_secondary")};
    font-family: ${({ theme }) => theme.resolve("font_family_primary")};
    font-size: ${({ theme }) => theme.resolve("font_size_sm")};
    font-weight: ${({ theme }) => theme.resolve("font_weight_regular")};
    line-height: ${({ theme }) => theme.resolve("line_height_sm")};
  }
`;

const MasterVariantBadge = ({
  pim_product_type,
}: {
  pim_product_type: PIMProductMinimal["pim_product_type"];
}) => {
  const { t } = useTranslation();
  if (pim_product_type === "master") {
    return (
      <MasterVariantBadgeContainer>
        <MasterProductIcon />
        <span>{t("Master")}</span>
      </MasterVariantBadgeContainer>
    );
  }
  if (pim_product_type === "variant") {
    return (
      <MasterVariantBadgeContainer>
        <VariantProductIcon />
        <span>{t("Variant")}</span>
      </MasterVariantBadgeContainer>
    );
  }
  return null;
};

interface CardInfoBlockProps {
  header: string;
  content: string | React.ReactNode;
  productId: string;
  fieldName: string;
  isDescription?: boolean;
}

const CardInfoBlock = ({
  header,
  content,
  productId,
  fieldName,
  isDescription = false,
}: CardInfoBlockProps) => {
  const tooltipId = `${productId}-${fieldName}`;
  const isOverflowing = typeof content === "string" && content.length > 50;

  return (
    <CardInfoBlockContainer>
      <CardInfoBlockHeader>{header}</CardInfoBlockHeader>
      {isDescription && typeof content === "string" ? (
        <CardInfoBlockContent
          isDescription={isDescription}
          dangerouslySetInnerHTML={{ __html: content }}
        />
      ) : (
        <CardInfoBlockContent
          isDescription={isDescription}
          data-tip={isOverflowing && !isDescription ? content : undefined}
          data-for={isOverflowing && !isDescription ? tooltipId : undefined}
        >
          {content}
        </CardInfoBlockContent>
      )}
      {isOverflowing && (
        <ReactTooltip
          id={tooltipId}
          place="top"
          type="dark"
          effect="solid"
          delayShow={500}
          multiline={true}
          className="card-tooltip"
        />
      )}
    </CardInfoBlockContainer>
  );
};

/* -------------------------------------------------------------------------- */
/*                              Helper Functions                              */
/* -------------------------------------------------------------------------- */

export const CardProduct = ({
  product,
  href,
  onProductClick,
  onQuoteClick,
  onSampleClick,
  onDocumentClick,
}: CardProductProps) => {
  const { t } = useTranslation();
  const {
    roleIsSomeKindOfSeller,
    roleIsSomeKindOfBuyer,
    roleIsGuest,
    hasPermission,
  } = useAuthContext();
  const { storefront_metadata } = useStoreState();
  const theme = useTheme();

  // Get product information
  const productName = product.name;

  // Extract values from card_data
  const productLine = product.card_data.product_line.value;
  const casNumber = product.card_data.cas_number.value;
  const chemicalName = product.card_data.chemical_name.value;
  const description = product.card_data.product_description.value;
  const brandName = product.card_data.brand_name.value;

  // Extract display names from card_data
  const casNumberHeader = product.card_data.cas_number.display_name;
  const chemicalNameHeader = product.card_data.chemical_name.display_name;
  const descriptionHeader = product.card_data.product_description.display_name;
  const brandNameHeader = product.card_data.brand_name.display_name;
  const productLineHeader = product.card_data.product_line.display_name;

  // Get document information from asset arrays
  const hasSDS = product.sds_assets.length > 0;
  const hasTDS = product.tds_assets.length > 0;
  const sdsCount = product.sds_assets.length;
  const tdsCount = product.tds_assets.length;

  // Check if product has documents - using available properties on PIMProductMinimal
  const hasDocumentsTab =
    product.sds_assets.length > 0 || product.tds_assets.length > 0;

  // Count total document types for "View All" button logic
  const hasMultipleDocuments = hasDocumentsTab;

  // Check if product has sample SKUs (from API response)
  const hasSampleSKUs = () => {
    return product.has_sample_sku;
  };

  // Determine storefront configuration for quotes and samples
  const quotingEnabled =
    storefront_metadata.quote_requests_visibility === "enabled";
  const samplingEnabled =
    storefront_metadata.sample_requests_visibility === "enabled";

  // Determine admin metadata visibility - only for internal users with edit permissions
  const showAdminMetadata =
    hasPermission("modify_products") && roleIsSomeKindOfSeller;

  // Buyer users - show Get Quote and Request Sample
  const showGetQuote = quotingEnabled && (roleIsGuest || roleIsSomeKindOfBuyer);
  const showRequestSample =
    samplingEnabled && (roleIsGuest || roleIsSomeKindOfBuyer);

  // For buyers, disable the sample request button when no sample SKUs are available
  const sampleRequestDisabled = roleIsSomeKindOfBuyer && !hasSampleSKUs();

  // Transaction seller users - show Create Quote and Create Sample
  const showCreateQuote =
    roleIsSomeKindOfSeller &&
    quotingEnabled &&
    product.pim_product_type !== "master";
  const showCreateSample =
    roleIsSomeKindOfSeller &&
    samplingEnabled &&
    hasSampleSKUs() &&
    product.pim_product_type !== "master";

  const handleCardClick = (e: React.MouseEvent) => {
    if (e.metaKey || e.ctrlKey || e.button === 1) {
      return;
    }

    e.preventDefault();
    onProductClick?.(product, e);
  };

  const createButtonClickHandler =
    (callback: () => void) => (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      callback();
    };

  const createSampleClickHandler =
    (callback: () => void, disabled: boolean) => (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled) {
        callback();
      }
    };

  const handleDocumentClick = (docType: "sds" | "tds" | "all") => {
    if (docType === "sds" && sdsCount === 1) {
      // Open single SDS document directly
      window.open(product.sds_assets[0].signed_url, "_blank");
      return;
    }

    if (docType === "tds" && tdsCount === 1) {
      // Open single TDS document directly
      window.open(product.tds_assets[0].signed_url, "_blank");
      return;
    }

    // Multiple documents or "View All" - let parent handle navigation
    onDocumentClick?.(product, docType);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <CardWrapper href={href} onClick={handleCardClick} data-card-wrapper="true">
      <ImageWrapper>
        {product.cover_image ? (
          <img src={product.cover_image} alt={productName || "Product"} />
        ) : (
          <PlaceholderImage>
            <PlaceholderLogo src={theme.portalLogoUrl} alt="Company Logo" />
          </PlaceholderImage>
        )}
        <MasterVariantBadge
          pim_product_type={product.pim_product_type ?? "standard"}
        />
      </ImageWrapper>

      <Body>
        {productName && <ProductName>{productName}</ProductName>}
        {showAdminMetadata && (
          <AdminMetadata>
            <AdminMetadataLeft>
              <AdminMetadataRow>
                <AdminMetadataIcon>
                  <CardCalendarIcon />
                </AdminMetadataIcon>
                {product.last_modified_full &&
                  formatDate(product.last_modified_full.modified_at)}
              </AdminMetadataRow>
              {(product.show_total_completion_score ||
                product.show_mandatory_completion_score) && (
                <AdminMetadataRow>
                  <AdminMetadataIcon>
                    <CardCompletionBadgeIcon />
                  </AdminMetadataIcon>
                  {product.show_total_completion_score &&
                  product.show_mandatory_completion_score
                    ? `${product.display_completion_score}% | ${product.display_mandatory_completion_score}%`
                    : product.show_total_completion_score
                    ? `${product.display_completion_score}%`
                    : `${product.display_mandatory_completion_score}%`}
                </AdminMetadataRow>
              )}
            </AdminMetadataLeft>
            <StatusContainer>
              <StatusLeft
                color={getProductStatusColor(product.display_status as any)}
                text={getProductStatusText(product.display_status as any, t)}
                productStatusType="product"
              />
            </StatusContainer>
          </AdminMetadata>
        )}
        {/* Document Badges */}
        {(hasSDS || hasTDS) && (
          <>
            <DocBadges>
              {hasSDS && (
                <DocBadge
                  onClick={createButtonClickHandler(() =>
                    handleDocumentClick("sds")
                  )}
                >
                  <DocIcon />
                  SDS
                </DocBadge>
              )}
              {hasTDS && (
                <DocBadge
                  onClick={createButtonClickHandler(() =>
                    handleDocumentClick("tds")
                  )}
                >
                  <DocIcon />
                  TDS
                </DocBadge>
              )}
              {hasMultipleDocuments && (
                <DocBadge
                  onClick={createButtonClickHandler(() =>
                    handleDocumentClick("all")
                  )}
                >
                  View All
                </DocBadge>
              )}
            </DocBadges>
          </>
        )}
        {/* Core Attributes */}
        <AttributesContainer>
          {casNumber && (
            <CardInfoBlock
              header={casNumberHeader}
              content={casNumber}
              productId={product.id}
              fieldName="cas"
            />
          )}
          {chemicalName && (
            <CardInfoBlock
              header={chemicalNameHeader}
              content={chemicalName}
              productId={product.id}
              fieldName="chemical-name"
            />
          )}
          {/* Product Identifier precedence: Brand Name > Product Line */}
          {brandName ? (
            <CardInfoBlock
              header={brandNameHeader}
              content={brandName}
              productId={product.id}
              fieldName="brand-name"
            />
          ) : (
            productLine && (
              <CardInfoBlock
                header={productLineHeader}
                content={productLine}
                productId={product.id}
                fieldName="product-line"
              />
            )
          )}

          {description && (
            <CardInfoBlock
              header={descriptionHeader as string}
              content={description}
              productId={product.id}
              fieldName="description"
              isDescription={true}
            />
          )}
        </AttributesContainer>
        {/* CTA Actions */}
        {(showGetQuote ||
          showRequestSample ||
          showCreateQuote ||
          showCreateSample) && (
          <ActionsWrapper>
            <Actions>
              {showGetQuote && (
                <PrimaryButtonSmallV2
                  onClick={createButtonClickHandler(() =>
                    onQuoteClick?.(product)
                  )}
                >
                  <PlusIcon />
                  {t("Get Quote")}
                </PrimaryButtonSmallV2>
              )}
              {showRequestSample && (
                <>
                  <SecondaryButtonSmallV2
                    disabled={sampleRequestDisabled}
                    data-tip={
                      sampleRequestDisabled
                        ? t("No sample SKUs are configured for this product")
                        : undefined
                    }
                    data-for={
                      sampleRequestDisabled
                        ? `sample-disabled-${product.id}`
                        : undefined
                    }
                    onClick={createSampleClickHandler(
                      () => onSampleClick?.(product),
                      sampleRequestDisabled
                    )}
                  >
                    {t("Request Sample")}
                  </SecondaryButtonSmallV2>
                  {sampleRequestDisabled && (
                    <ReactTooltip
                      id={`sample-disabled-${product.id}`}
                      place="top"
                      type="dark"
                      effect="solid"
                      delayShow={500}
                    />
                  )}
                </>
              )}
              {showCreateQuote && (
                <PrimaryButtonSmallV2
                  onClick={createButtonClickHandler(() =>
                    onQuoteClick?.(product)
                  )}
                >
                  <PlusIcon />
                  {t("Create Quote")}
                </PrimaryButtonSmallV2>
              )}
              {showCreateSample && (
                <SecondaryButtonSmallV2
                  onClick={createSampleClickHandler(
                    () => onSampleClick?.(product),
                    false
                  )}
                >
                  {t("Create Sample")}
                </SecondaryButtonSmallV2>
              )}
            </Actions>
          </ActionsWrapper>
        )}
      </Body>
    </CardWrapper>
  );
};
